import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import {
  Heart, Bone, Brain, Baby, Scissors, Activity, Leaf, Sparkles,
  Stethoscope, Building2, ClipboardList, Plane, Home, HeartPulse,
  CheckCircle, Globe2, DollarSign, Clock, Languages, Shield,
  ArrowRight, Star, Upload, Palmtree, Waves, PlaneTakeoff
} from "lucide-react";

const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/a736bd0af66d8e9f15dad83c033881ee288f185a2f582c1cd8c7f415aca14aa8.png",
  globe: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/157d58af0c873689086ab59e871ccbef313009bf79a9876c49d9d65e611c1cda.png",
  abstract: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/1040d868ded8146fb2c39052a72bf7c9dd50d06fe5092c540808a6a8e6397397.png",
  consultation: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/24ef8d8e53f3568e65962aae65157dfc97dad17d6df9af2c0097ea910361284d.png",
  airport: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/2b1b64e33b18c0ed999e1003976963c47fbb8f394e85da4decbbeda6926cba21.png",
  hotel: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/0d5409d868cbb96bdfdf01c13e33b19ae9d0bd370464126b5b99f45ccf8e4e37.png",
  visa: "https://images.unsplash.com/photo-1454496406107-dc34337da8d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHx2aXNhJTIwcGFzc3BvcnQlMjB0cmF2ZWwlMjBkb2N1bWVudHMlMjBhc3Npc3RhbmNlfGVufDB8fHx8MTc3NjI1MjMxOHww&ixlib=rb-4.1.0&q=85",
  recovery: "https://images.unsplash.com/photo-1632053002434-3e96813a3475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  doctors: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/6603e5be2bbeb9475f504eafe2fffe0b588af7b246a88119e28b1b4eec2ceb62.png",
  planning: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/253296de4a2764ae13cc4097cef16c5f60ffcabb0ddaf24563aed42375eff86e.png",
  nurse: "https://static.prod-images.emergentagent.com/jobs/3fcdf28a-afce-4eb0-bade-88157aa8ea3c/images/aadfbf7eaa969fdec81c1ff277ced0d0d363068a92350d0a03a41d674fe47d10.png",
};

const svcIcons = [Stethoscope, Building2, ClipboardList, Plane, Home, HeartPulse];
const svcImages = [IMAGES.consultation, IMAGES.doctors, IMAGES.planning, IMAGES.airport, IMAGES.hotel, IMAGES.nurse];
const specIcons = [Stethoscope, Leaf, Sparkles, Activity, Heart, HeartPulse];
const specKeys = ["modern", "ayurveda", "wellness", "rehab", "specialty", "integrative"];
const specColors = ["bg-blue-50 text-blue-600", "bg-green-50 text-green-700", "bg-purple-50 text-purple-600", "bg-amber-50 text-amber-600", "bg-pink-50 text-pink-600", "bg-teal-50 text-teal-600"];

const specLinks = {
  modern: "/healthcare/modern-medicine",
  ayurveda: "/healthcare/ayurveda",
  wellness: "/healthcare/wellness",
  rehab: "/healthcare/rehabilitation",
  specialty: "/healthcare/specialty-clinics",
  integrative: "/healthcare/integrative-medicine",
};

const specData = {
  modern: { t: "Modern Medicine", d: "Cardiology, Orthopedics, Oncology, Neurology" },
  ayurveda: { t: "Ayurveda", d: "Panchakarma, Abhyanga, Shirodhara, traditional herbal therapies" },
  wellness: { t: "Wellness", d: "Preventive checkups, lifestyle detox, yoga & meditation programs" },
  rehab: { t: "Rehabilitation", d: "Physical therapy, post-operative recovery, neurological rehabilitation" },
  specialty: { t: "Specialty Clinics", d: "IVF & Fertility, Cosmetic Surgery, Dental, Eye Care, Weight Loss, Dermatology" },
  integrative: { t: "Integrative Medicine", d: "Modern medicine combined with Ayurveda, yoga and nutrition for whole-body healing" },
};
const whyIcons = [Shield, DollarSign, Globe2, Languages, Clock];

const testimonials = [
  { name: "Sarah M.", country: "Nigeria", treatment: "Hip Replacement", quote: "Flarial MediTours made my entire medical journey seamless. From airport pickup to surgery and recovery, everything was handled perfectly. I saved over 80% compared to costs back home.", rating: 5 },
  { name: "Ahmed K.", country: "UAE", treatment: "Heart Bypass Surgery", quote: "The coordination between the hospital and the Flarial team was exceptional. My surgeon was one of the best in Kerala, India, and the entire experience exceeded my expectations.", rating: 5 },
  { name: "Maria L.", country: "Kenya", treatment: "Cancer Treatment", quote: "I was nervous about traveling for treatment, but Flarial's team was with me every step of the way. The hospital facilities were world-class and the care was outstanding.", rating: 5 },
];

const hospitals = [
  { name: "KIMS Hospital", location: "Thiruvananthapuram", accreditation: "NABH" },
  { name: "Aster Medcity", location: "Kochi", accreditation: "JCI & NABH" },
  { name: "Amrita Hospital", location: "Kochi", accreditation: "NABH" },
  { name: "Kottakkal AVS", location: "Malappuram", accreditation: "NABH (Ayurveda)" },
];

export default function HomePage() {
  const { t } = useLanguage();
  const svcKeys = ["s1", "s2", "s3", "s4", "s5", "s6"];
  const stepKeys = ["s1", "s2", "s3", "s4", "s5", "s6"];
  const whyKeys = ["w1", "w2", "w3", "w4", "w5"];

  return (
    <div data-testid="home-page">
      <SEO path="/" />

      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ backgroundImage: `url(${IMAGES.hero})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
          <div className="max-w-2xl">
            <h1 className="animate-fade-in-up animation-delay-100 font-outfit text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#03045e] leading-tight mb-3">
              {t("hero.headline")} <span className="font-medium text-[#00A4A9]">{t("hero.highlight")}</span>
            </h1>
            <p className="animate-fade-in-up animation-delay-150 text-lg sm:text-xl text-[#00A4A9] italic font-medium mb-6 tracking-wide">
              Expert Care. Effortless Journey.
            </p>
            <p className="animate-fade-in-up animation-delay-200 text-base sm:text-lg text-slate-600 leading-relaxed mb-10 max-w-xl">
              {t("hero.subtext")}
            </p>
            <div className="animate-fade-in-up animation-delay-300 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-consultation-btn">
                <Button size="lg" className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white px-8 h-12 text-base btn-hover">
                  {t("hero.cta1")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-upload-btn">
                <Button size="lg" variant="outline" className="rounded-full border-[#03045e] text-[#03045e] hover:bg-[#03045e] hover:text-white px-8 h-12 text-base btn-hover">
                  <Upload className="w-4 h-4 mr-2" /> {t("hero.cta2")}
                </Button>
              </Link>
            </div>
            <div className="animate-fade-in-up animation-delay-400 mt-12 hidden flex-wrap gap-6 sm:gap-12">
              {[["5,000+", t("hero.stat1")], ["50+", t("hero.stat2")], ["15+", t("hero.stat3")]].map(([num, label]) => (
                <div key={num}><div className="font-outfit text-2xl sm:text-3xl font-semibold text-[#03045e]">{num}</div><div className="text-xs sm:text-sm text-slate-500 mt-1">{label}</div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services with images */}
      <section data-testid="services-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="overline">{t("sections.services")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight">{t("sections.servicesTitle")}</h2>
              <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t("sections.servicesDesc")}</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {svcKeys.map((key, i) => {
              const Icon = svcIcons[i];
              const img = svcImages[i];
              return (
                <FadeIn key={key} delay={i * 80}>
                  <div className={`bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${i === 0 || i === 4 ? "md:col-span-2" : ""}`} data-testid={`service-card-${i}`}>
                    {img && (
                      <div className="h-44 overflow-hidden"><img src={img} alt={t(`svc.${key}.t`)} className="w-full h-full object-cover" /></div>
                    )}
                    <div className="p-8">
                      <div className="w-12 h-12 rounded-xl bg-[#e0f2fe] flex items-center justify-center mb-5">
                        <Icon className="w-6 h-6 text-[#00A4A9]" />
                      </div>
                      <h3 className="font-outfit text-xl font-medium text-[#03045e] mb-3">{t(`svc.${key}.t`)}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{t(`svc.${key}.d`)}</p>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialties */}
      <section data-testid="specialties-section" className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <span className="overline">{t("sections.specialties")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight">{t("sections.specialtiesTitle")}</h2>
              <p className="text-slate-600 mt-4 text-base leading-relaxed">{t("sections.specialtiesDesc")}</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {specKeys.map((key, i) => {
              const Icon = specIcons[i];
              const data = specData[key];
              return (
                <FadeIn key={key} delay={i * 80}>
                  <div className="bg-white border border-slate-200 rounded-2xl p-7 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`specialty-card-${i}`}>
                    <div className={`w-12 h-12 rounded-xl ${specColors[i]} flex items-center justify-center mb-4`}><Icon className="w-6 h-6" /></div>
                    <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{data.t}</h3>
                    <p className="text-slate-500 text-sm">{data.d}</p>
                    <Link to={specLinks[key] || "/treatments"} className="inline-flex items-center text-[#00A4A9] text-sm font-medium mt-4 hover:gap-2 transition-all">
                      {t("sections.learnMore")} <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Kerala */}
      <section data-testid="why-kerala-section" className="section-padding bg-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#90e0ef]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full bg-[#caf0f8]/25 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14 max-w-3xl mx-auto">
              <span className="overline">{t("whyKerala.pill")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight leading-tight">{t("whyKerala.title")}</h2>
              <p className="text-slate-600 mt-5 text-base leading-relaxed">{t("whyKerala.desc")}</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {[
              { k: "r1", Icon: Leaf, color: "from-[#e0f2fe] to-white", iconColor: "text-[#00A4A9]" },
              { k: "r2", Icon: Shield, color: "from-[#caf0f8]/60 to-white", iconColor: "text-[#023e8a]" },
              { k: "r3", Icon: DollarSign, color: "from-[#e0f2fe] to-white", iconColor: "text-[#00A4A9]" },
              { k: "r4", Icon: Languages, color: "from-[#caf0f8]/60 to-white", iconColor: "text-[#023e8a]" },
              { k: "r5", Icon: Waves, color: "from-[#e0f2fe] to-white", iconColor: "text-[#00A4A9]" },
              { k: "r6", Icon: PlaneTakeoff, color: "from-[#caf0f8]/60 to-white", iconColor: "text-[#023e8a]" },
            ].map(({ k, Icon, color, iconColor }, i) => (
              <FadeIn key={k} delay={i * 60}>
                <div
                  data-testid={`why-kerala-card-${i}`}
                  className={`relative h-full bg-gradient-to-br ${color} border border-slate-100 rounded-2xl p-6 lg:p-7 hover:border-[#00A4A9]/30 hover:shadow-[0_14px_44px_rgb(3,4,94,0.08)] transition-all`}
                >
                  <div className="w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center mb-4">
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{t(`whyKerala.${k}.t`)}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{t(`whyKerala.${k}.d`)}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section data-testid="how-it-works-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14 max-w-3xl mx-auto">
              <span className="overline">{t("sections.howItWorks")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight">{t("sections.howItWorksTitle")}</h2>
              <p className="text-slate-600 mt-5 text-base leading-relaxed">{t("sections.howItWorksDesc")}</p>
            </div>
          </FadeIn>

          {/* USP Callout */}
          <FadeIn>
            <div className="mb-14 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#03045e] to-[#023e8a] p-8 sm:p-10 lg:p-12" data-testid="usp-callout">
              <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#00A4A9]/25 blur-3xl" />
              <div className="absolute -left-10 -bottom-10 w-56 h-56 rounded-full bg-[#48cae4]/20 blur-3xl" />
              <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
                <div>
                  <Badge className="mb-4 bg-[#48cae4]/20 text-[#90e0ef] hover:bg-[#48cae4]/20 border-0 rounded-full px-3 py-1 text-[11px] font-semibold tracking-wider">
                    {t("usp.pill")}
                  </Badge>
                  <h3 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-tight mb-4">
                    {t("usp.title")}
                  </h3>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-2xl">
                    {t("usp.desc")}
                  </p>
                </div>
                <div className="flex lg:flex-col gap-3">
                  {["Single Coordinator", "Transparent Pricing"].map((chip) => (
                    <span key={chip} className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-xs text-white whitespace-nowrap">
                      <CheckCircle className="w-3.5 h-3.5 text-[#90e0ef]" /> {chip}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {stepKeys.map((key, i) => (
              <FadeIn key={key} delay={i * 100}>
                <div className="relative h-full" data-testid={`step-card-${i}`}>
                  <div className="flex items-start gap-5 h-full bg-white border border-slate-100 rounded-2xl p-6 hover:border-[#00A4A9]/40 hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)] transition-all">
                    <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[#00A4A9] flex items-center justify-center">
                      <span className="font-outfit text-lg font-bold text-white">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div>
                      <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{t(`steps.${key}.t`)}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{t(`steps.${key}.d`)}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Kerala Stay & Recovery */}
      <section data-testid="kerala-stay-section" className="section-padding bg-gradient-to-b from-[#F9F9F8] to-[#e0f2fe]/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <FadeIn>
              <div>
                <span className="overline">{t("kerala.pill")}</span>
                <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 mb-5 tracking-tight leading-tight">{t("kerala.title")}</h2>
                <p className="text-slate-600 text-base leading-relaxed mb-8 max-w-xl">{t("kerala.desc")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {["f1", "f2", "f3", "f4"].map((fk, i) => (
                    <div key={fk} className="flex gap-3 items-start" data-testid={`kerala-feature-${i}`}>
                      <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#00A4A9]/10 flex items-center justify-center">
                        <Leaf className="w-4 h-4 text-[#00A4A9]" />
                      </div>
                      <div>
                        <h4 className="font-outfit font-medium text-[#03045e] text-sm mb-1">{t(`kerala.${fk}.t`)}</h4>
                        <p className="text-slate-500 text-xs leading-relaxed">{t(`kerala.${fk}.d`)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img src={IMAGES.hotel} alt="Kerala homestay recovery" className="w-full h-[380px] sm:h-[460px] object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#03045e]/40 via-transparent to-transparent" />
                </div>
                <div className="absolute -bottom-6 -left-6 sm:-left-8 bg-white rounded-2xl p-5 shadow-xl border border-slate-100 max-w-[240px] hidden">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#00A4A9]/10 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-[#00A4A9]" />
                    </div>
                    <div className="font-outfit font-medium text-[#03045e] text-sm">Backwaters & Ayurveda</div>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">Kerala's natural healing environment — included in every recovery package.</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeIn>
              <div>
                <span className="overline">{t("sections.whyUs")}</span>
                <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 mb-8 tracking-tight">{t("sections.whyUsTitle")}</h2>
                <div className="space-y-6">
                  {whyKeys.map((key, i) => {
                    const Icon = whyIcons[i];
                    return (
                      <div key={key} className="flex gap-4 items-start">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#00A4A9]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#00A4A9]" /></div>
                        <div>
                          <h4 className="font-outfit font-medium text-[#03045e] mb-1">{t(`why.${key}.t`)}</h4>
                          <p className="text-slate-500 text-sm">{t(`why.${key}.d`)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="relative"><img src={IMAGES.globe} alt="Global medical tourism network" className="rounded-3xl w-full" /></div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Testimonials hidden until real patient stories are collected */}
      <section data-testid="testimonials-section" className="hidden">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="overline">{t("sections.testimonials")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight">{t("sections.testimonialsTitle")}</h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((tm, i) => (
              <FadeIn key={tm.name} delay={i * 100}>
                <div className="bg-white border border-slate-200 rounded-2xl p-8 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`testimonial-card-${i}`}>
                  <div className="flex gap-1 mb-4">{Array.from({ length: tm.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6 italic">"{tm.quote}"</p>
                  <div className="border-t border-slate-100 pt-4">
                    <div className="font-outfit font-medium text-[#03045e]">{tm.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{tm.country} &middot; {tm.treatment}</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Hospitals — hidden until partnerships are confirmed */}
      <section data-testid="hospitals-section" className="hidden">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="overline">{t("sections.hospitals")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-[#03045e] mt-3 tracking-tight">{t("sections.hospitalsTitle")}</h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {hospitals.map((h, i) => (
              <FadeIn key={h.name} delay={i * 80}>
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`hospital-card-${i}`}>
                  <div className="h-36 bg-gradient-to-br from-[#e0f2fe] to-[#F1F5F9] flex items-center justify-center"><Building2 className="w-10 h-10 text-[#00A4A9]/40" /></div>
                  <div className="p-5">
                    <h3 className="font-outfit font-medium text-[#03045e] mb-1">{h.name}</h3>
                    <p className="text-xs text-slate-500 mb-2">{h.location}, Kerala</p>
                    <Badge variant="outline" className="text-xs border-[#00A4A9]/30 text-[#00A4A9]">{h.accreditation}</Badge>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={300}>
            <div className="text-center mt-10">
              <Link to="/hospitals" data-testid="view-all-hospitals-btn">
                <Button variant="outline" className="rounded-full border-[#03045e] text-[#03045e] hover:bg-[#03045e] hover:text-white">
                  {t("sections.viewAll")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="relative section-padding overflow-hidden hidden">
        <div className="absolute inset-0 bg-[#03045e]" />
        <div className="absolute inset-0 opacity-10"><img src={IMAGES.abstract} alt="" className="w-full h-full object-cover" /></div>
        <div className="relative max-w-4xl mx-auto text-center">
          <FadeIn>
            <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-medium text-white tracking-tight mb-5">{t("sections.cta")}</h2>
            <p className="text-slate-300 text-base sm:text-lg mb-10 max-w-2xl mx-auto">{t("sections.ctaDesc")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="cta-consultation-btn">
                <Button size="lg" className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white px-8 h-12 text-base btn-hover">
                  {t("hero.cta1")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href={buildWhatsAppUrl("Hi, I'd like a free consultation.")} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn">
                <Button size="lg" variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 px-8 h-12 text-base">
                  {t("sections.chatWhatsApp")}
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 text-sm text-slate-400">
              <span className="flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4 text-[#00A4A9]" /> {t("sections.freeConsult")}</span>
              <span className="flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4 text-[#00A4A9]" /> {t("sections.noObligation")}</span>
              <span className="flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4 text-[#00A4A9]" /> {t("sections.fastResponse")}</span>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
